
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
    },
});

export async function signInWithGoogle(redirectTo?: string) {
    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: redirectTo || `${window.location.origin}/dashboard`,
            queryParams: {
                access_type: 'offline',
                prompt: 'select_account',
            },
        }
    });
    if (error) throw error;
    return data;
}

export async function signInWithFacebook(redirectTo?: string) {
    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
            redirectTo: redirectTo || `${window.location.origin}/dashboard`,
            scopes: 'email',
        }
    });
    if (error) throw error;
    return data;
}

export async function signUp(email: string, password: string, metadata?: {
    firstName?: string;
    lastName?: string;
    role?: string;
}) {
    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
            emailRedirectTo: `${window.location.origin}/confirm-email-success`,
            data: {
                first_name: metadata?.firstName || '',
                last_name: metadata?.lastName || '',
                role: metadata?.role || 'TRAINER',
            }
        }
    });

    if (error) throw error;
    return data;
}

export async function signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    if (error) throw error;
    return data;
}

// ============================================================
// Real-time Chat Functions
// ============================================================

/**
 * Subscribe to new chat messages in a conversation
 */
export function subscribeToMessages(
    userId1: string,
    userId2: string,
    callback: (message: any) => void
) {
    // Create a canonical room name to avoid duplicates
    const roomId = [userId1, userId2].sort().join('_');
    const channelName = `messages_${roomId}`;

    // console.log(`Subscribing to channel: ${channelName}`);

    const userId1Str = String(userId1);
    const userId2Str = String(userId2);
    const seenMessageIds = new Set<string>();

    const handleIncomingMessage = (payload: any) => {
        const msg = payload.new as any;

        const senderId = String(msg.senderId || msg.sender_id || '');
        const receiverId = String(msg.receiverId || msg.receiver_id || '');

        if (!senderId || !receiverId) return;

        const isRelevant =
            (senderId === userId1Str && receiverId === userId2Str) ||
            (senderId === userId2Str && receiverId === userId1Str);

        if (isRelevant) {
            if (seenMessageIds.has(msg.id)) return;
            seenMessageIds.add(msg.id);

            const message = transformMessageFromDB(msg);
            if (message) callback(message);
        }
    };

    // No server-side filter: Realtime lowercases identifiers and can't match the
    // camelCase columns (senderId/receiverId), while snake_case columns don't exist.
    // Filtering is done in handleIncomingMessage via isRelevant.
    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'chat_messages',
            },
            handleIncomingMessage
        )
        .subscribe();

    return () => {
        supabase.removeChannel(subscription);
    };
}

/**
 * Subscribe to typing indicators
 */
export function subscribeToTypingIndicator(
    userId1: string,
    userId2: string,
    callback: (isTyping: boolean, userId: string) => void
) {
    const roomId = [userId1, userId2].sort().join('_');

    const subscription = supabase
        .channel(`typing_${roomId}`)
        .on('broadcast', { event: 'typing' }, (payload) => {
            callback(payload.payload.isTyping, payload.payload.userId);
        })
        .subscribe();

    return () => {
        supabase.removeChannel(subscription);
    };
}

/**
 * Broadcast typing indicator to other user
 */
export async function broadcastTyping(userId: string, conversationId: string, isTyping: boolean) {
    const roomId = [userId, conversationId].sort().join('_');

    await supabase
        .channel(`typing_${roomId}`)
        .send({
            type: 'broadcast',
            event: 'typing',
            payload: { isTyping, userId }
        })
        .catch((err) => console.warn('[Supabase] Typing broadcast failed:', err));
}

// ============================================================
// Presence Functions (online status)
// ============================================================
/**
 * Subscribe to a presence channel that tracks online users.
 * Calls `onSync` with an array of user IDs that are currently online.
 */
export function subscribeToPresence(onSync: (onlineUserIds: string[]) => void) {
    const channel = supabase.channel('online_presence')
        .on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState() as Record<string, Array<{ userId?: string; user_id?: string }> | undefined>;
            const onlineIds = new Set<string>();

            Object.values(state).forEach((entries) => {
                (entries || []).forEach((entry) => {
                    const trackedUserId = String(entry?.userId || entry?.user_id || '').trim();
                    if (trackedUserId) {
                        onlineIds.add(trackedUserId);
                    }
                });
            });

            onSync(Array.from(onlineIds));
        })
        .subscribe(async (status) => {
            if (status === 'SUBSCRIBED') {
                // Track current user as online
                const { data: { user } } = await supabase.auth.getUser();
                if (user?.id) {
                    await channel.track({ online_at: new Date().toISOString(), userId: user.id });
                }
            }
        });

    return () => {
        supabase.removeChannel(channel);
    };
}


/**
 * Transform database record to UI format
 */
export function transformMessageFromDB(record: any) {
    if (!record) return null;

    const senderId = record.senderId || record.sender_id;
    const receiverId = record.receiverId || record.receiver_id;

    if (!senderId || !receiverId) return null;

    let parsedReplyTo = record.replySnapshot || record.reply_snapshot;
    if (typeof parsedReplyTo === 'string') {
        try { parsedReplyTo = JSON.parse(parsedReplyTo); } catch (e) { parsedReplyTo = undefined; }
    }

    return {
        id: record.id,
        senderId,
        receiverId,
        content: record.content || '',
        imageUrl: record.imageUrl || record.image_url,
        imageMetadata: record.imageMetadata || record.image_metadata,
        videoUrl: record.videoUrl || record.video_url,
        videoMetadata: record.videoMetadata || record.video_metadata,
        timestamp: record.timestamp || record.created_at || new Date().toISOString(),
        isRead: record.isRead ?? record.is_read ?? false,
        senderName: '', // Will be filled by UI mapping if needed
        isMe: false, // Will be calculated in UI
        replyToId: record.replyToId || record.reply_to_id,
        replyTo: parsedReplyTo,
    };
}

export async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}
